import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { createBloodPressure, updateBloodPressure } from '../../../../store/participantService';
import { todayDate } from '../../../../utils/dateFunctions';
import { debounce } from '../../../../utils/debounce';
import { getObjFromId } from '../../../../utils/directusFunctions';
import { regExps } from '../../../../utils/formValidation/validationPatterns';
import DateSelect from '../../../formElements/DateSelect';
import SingleSelect from '../../../formElements/SingleSelect';
import TextInputField from '../../../formElements/TextInputField';
import FormActions from '../../../ui/formActions/FormActions';

import { RANGE_WARNING_CONTENT, ranges, validate } from './validateBloodPressure';

import app from '../../../../app.module.css';
import form from '../../../../commonStyles/formStyles.module.css';

const _WARNING_INTERVAL = 1000;

const EditBloodPressure = ({ participant, onClose, isDisabled, formType, row = {} }) => {
    const dispatch = useDispatch();

    // LOCAL STATE
    const _MESSAGE = `Participant blood pressure entry has been ${
        formType === 'create' ? 'added' : 'updated'
    }`;

    const initialState = {
        id: '',
        participantId: '',
        eventDate: todayDate(),
        systolic: '',
        diastolic: '',
        measurementId: '',
        measurement: ''
    };
    const initialWarningState = { systolic: false, diastolic: false };
    const [newEntry, setNewEntry] = useState(initialState);
    const [showWarning, setShowWarning] = useState(initialWarningState);
    const [errors, setErrors] = useState({});
    const [isButtonDisabled, setIsButtonDisabled] = useState(false);

    // STORE STATE
    const measurementTypes = useSelector(
        (state) => state.entities.directusService.measurementTypes
    );

    // USEEFFECTS
    useEffect(() => {
        return () => configWarning.current.cancel?.();
    }, []);

    useEffect(() => {
        if (Object.keys(row).length < 1 || formType !== 'edit') return;
        setNewEntry({ ...row });
        checkRange('systolic', row.systolic);
        checkRange('diastolic', row.diastolic);
    }, [row, formType]);

    useEffect(() => {
        setIsButtonDisabled(Object.values(errors).some((el) => el.error));
    }, [errors]);

    // HELPER FNs
    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { ...errors[key], error: false } }));
    };

    const checkRange = (key, value) => {
        setShowWarning((prev) => ({
            ...prev,
            [key]: value && (value <= ranges[key].min || value >= ranges[key].max)
        }));
    };

    const configWarning = useRef(
        debounce((key, value) => checkRange(key, value), _WARNING_INTERVAL)
    );

    // EVENT HANDLERS
    const onUpdate = (key, value) => {
        clearError(key);
        setNewEntry((prev) => ({ ...prev, [key]: value }));
    };

    const onPressureValueChange = (key, value) => {
        onUpdate(key, value.replace(regExps.DIGITS_R, ''));
        configWarning.current(key, value);
    };

    const onSubmit = (e) => {
        e.preventDefault();
        const newErrors = validate(newEntry, row, formType);
        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }
        // eslint-disable-next-line
        const { measurement, isWarning, ...rest } = newEntry;
        const payload = { ...rest, participantId: participant?.id };
        formType === 'create'
            ? dispatch(createBloodPressure(payload, _MESSAGE))
            : dispatch(updateBloodPressure(payload.id, payload, _MESSAGE));
        onClose();
    };

    // RENDER
    return (
        <div className={`${form.formWrapper} ${app.fadein}`}>
            <form className={form.form} onSubmit={onSubmit} data-testid="edit_blood_pressure_form">
                <h2 className={app.sectionHeading}>
                    {formType === 'create' ? 'Add a New Blood Pressure' : 'Edit Blood Pressure'}
                </h2>
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <DateSelect
                            id="eventDate"
                            label="Event Date"
                            value={newEntry.eventDate}
                            disabled={isDisabled}
                            mandatory={true}
                            error={errors.eventDate}
                            onDateChange={(date) => onUpdate('eventDate', date)}
                        />
                    </div>
                </div>

                <div className={form.fullWidth}>
                    <TextInputField
                        id="systolic"
                        label="Systolic"
                        placeholder="Enter systolic value..."
                        mandatory={true}
                        disabled={isDisabled}
                        maxLength={3}
                        customClass="flexWidth"
                        value={newEntry.systolic || ''}
                        error={errors.systolic}
                        warning={showWarning.systolic ? RANGE_WARNING_CONTENT : ''}
                        onChange={(e) => onPressureValueChange('systolic', e.target.value)}
                    />

                    <div className={form.formColumnDivideSign}>/</div>

                    <TextInputField
                        id="diastolic"
                        label="Diastolic"
                        placeholder="Enter diastolic value..."
                        mandatory={true}
                        disabled={isDisabled}
                        maxLength={3}
                        customClass="flexWidth"
                        value={newEntry.diastolic}
                        error={errors.diastolic}
                        warning={showWarning.diastolic ? RANGE_WARNING_CONTENT : ''}
                        onChange={(e) => onPressureValueChange('diastolic', e.target.value)}
                    />
                </div>

                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'measurementId'}
                            label={'Measurement'}
                            placeholder="Select measurement type..."
                            disabled={isDisabled}
                            mandatory={true}
                            menuItems={measurementTypes || []}
                            selectedId={newEntry.measurementId}
                            selected={getObjFromId(measurementTypes, newEntry.measurementId)}
                            error={errors.measurementId}
                            onChange={(chosenId) => onUpdate('measurementId', chosenId)}
                        />
                    </div>
                    <div className={form.formColumn}></div>
                </div>

                <FormActions
                    onClose={onSubmit}
                    onCancel={onClose}
                    disabled={isButtonDisabled || isDisabled}
                    btnText={formType === 'create' ? 'Save' : 'Update'}
                    customClass="noTopBorder"
                    error={errors.button}
                    clearError={() => clearError('button')}
                />
            </form>
        </div>
    );
};

EditBloodPressure.propTypes = {
    participant: PropTypes.object.isRequired,
    onClose: PropTypes.func,
    isDisabled: PropTypes.bool,
    formType: PropTypes.string,
    row: PropTypes.object
};

export default EditBloodPressure;
